<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="text-left card-header fixed-card-title2">
			<v-row no-gutters>
				<v-col cols="6">
					<v-toolbar-title>{{ $t('edit') }} {{ $t('item') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
			</v-row>
		</v-card-title>
		<v-card-text class="pa-4 pb-0 overflow-y-auto" style="height: calc(100vh - 122px)">
			<v-row dense>
				<v-col cols="12">
					<v-select
						v-model="editingItem.itemModelId"
						:label="$t('item-model')"
						:items="itemCatalogs"
						item-text="name"
						item-value="id"
						dense
						outlined
						hide-details
						:menu-props="{ offsetY: true }" />
				</v-col>
				<v-col cols="12">
					<v-select
						v-model="editingItem.status"
						:label="$t('status')"
						:items="itemStatusOptions"
						item-text="description"
						item-value="code"
						dense
						outlined
						hide-details
						:menu-props="{ offsetY: true }" />
				</v-col>
				<v-col cols="12">
					<v-select
						v-model="editingItem.condition"
						:label="$t('condition')"
						:items="itemConditionOptions"
						item-text="description"
						item-value="code"
						dense
						outlined
						hide-details
						:menu-props="{ offsetY: true }" />
				</v-col>
				<v-col cols="12">
					<v-select
						hide-details
						dense
						v-model="editingItem.organizationId"
						:items="medicalInstitutions"
						item-value="id"
						item-text="institutionName"
						:label="$t('medicalInstitution')"
						outlined
						:menu-props="{ offsetY: true }" />
				</v-col>
				<v-col cols="12">
					<v-select
						hide-details
						dense
						:disabled="!editingItem.organizationId"
						v-model="editingItem.locationId"
						:items="hospitals.filter((hospital) => hospital.medicalInstitutionId === editingItem.organizationId)"
						item-value="id"
						item-text="hospitalName"
						:label="$t('hospital')"
						outlined
						:menu-props="{ offsetY: true }" />
				</v-col>
				<v-col cols="12">
					<v-text-field v-model="editingItem.serialNumber" :label="$t('serial-number')" dense outlined hide-details />
				</v-col>
				<v-col cols="12">
					<v-text-field v-model="editingItem.assetTag" :label="$t('asset-tag')" dense outlined hide-details />
				</v-col>
				<v-col cols="12">
					<v-text-field
						v-model="editingItem.remoteConnectIdString"
						:label="$t('remote-connect-id')"
						dense
						outlined
						hide-details />
				</v-col>
				<v-col cols="6">
					<v-menu
						ref="priceDateMenu"
						v-model="priceDateMenu"
						:close-on-content-click="false"
						transition="scale-transition"
						offset-y
						min-width="auto">
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								:value="formattedPurchaseDate"
								:label="$t('purchase-date')"
								prepend-inner-icon="mdi-calendar"
								readonly
								outlined
								dense
								hide-details
								v-bind="attrs"
								v-on="on" />
						</template>
						<v-date-picker
							v-model="editingItem.purchaseDate"
							no-title
							:max="new Date().toISOString().substring(0, 10)"
							@input="priceDateMenu = false" />
					</v-menu>
				</v-col>
				<v-col cols="6">
					<v-text-field
						v-model="editingItem.purchasePrice"
						:label="$t('purchase-price')"
						dense
						outlined
						inputmode="decimal"
						:rules="priceRules"
						@input="onPurchasePriceInput"
						@keypress="(e) => /[0-9.]/.test(e.key) || e.preventDefault()"
						hide-details />
				</v-col>
				<v-col cols="12">
					<v-text-field
						v-model="editingItem.purchaseCurrency"
						:label="$t('purchase-currency')"
						dense
						outlined
						hide-details />
				</v-col>
				<v-col cols="12">
					<v-menu
						ref="warrantyEndDateMenu"
						v-model="warrantyEndDateMenu"
						:close-on-content-click="false"
						transition="scale-transition"
						offset-y
						min-width="auto">
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								:value="formattedWarrantyEndDate"
								:label="$t('warranty-end-date')"
								prepend-inner-icon="mdi-calendar"
								readonly
								outlined
								dense
								hide-details
								v-bind="attrs"
								v-on="on" />
						</template>
						<v-date-picker v-model="editingItem.warrantyEndDate" no-title @input="warrantyEndDateMenu = false" />
					</v-menu>
				</v-col>
				<v-col cols="6">
					<v-text-field
						v-model="editingItem.sellPrice"
						:label="$t('selling-price')"
						dense
						outlined
						inputmode="decimal"
						:rules="priceRules"
						@input="onSellingPriceInput"
						@keypress="(e) => /[0-9.]/.test(e.key) || e.preventDefault()"
						hide-details />
				</v-col>
				<v-col cols="6">
					<v-text-field
						v-model="editingItem.sellCurrency"
						:label="$t('selling-currency')"
						dense
						outlined
						hide-details />
				</v-col>
				<v-col cols="12">
					<v-text-field
						v-model="editingItem.trackingNumber"
						:label="$t('tracking-number')"
						dense
						outlined
						hide-details />
				</v-col>
				<v-col cols="12">
					<v-menu
						ref="shipmentDateMenu"
						v-model="shipmentDateMenu"
						:close-on-content-click="false"
						transition="scale-transition"
						offset-y
						min-width="auto">
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								:value="formattedShipmentDate"
								:label="$t('shipment-date')"
								prepend-inner-icon="mdi-calendar"
								readonly
								outlined
								dense
								hide-details
								v-bind="attrs"
								v-on="on" />
						</template>
						<v-date-picker v-model="editingItem.shipmentDate" no-title @input="shipmentDateMenu = false" />
					</v-menu>
				</v-col>
				<v-col cols="6">
					<v-text-field
						v-model="editingItem.shipmentCost"
						:label="$t('shipping-cost')"
						dense
						outlined
						inputmode="decimal"
						:rules="priceRules"
						@input="onShipmentPriceInput"
						@keypress="(e) => /[0-9.]/.test(e.key) || e.preventDefault()"
						hide-details />
				</v-col>
				<v-col cols="6">
					<v-text-field
						v-model="editingItem.shipmentCurrency"
						:label="$t('shipping-currency')"
						dense
						outlined
						hide-details />
				</v-col>
				<v-col cols="12">
					<v-textarea v-model="editingItem.note" :label="$t('notes')" dense outlined hide-details no-resize />
				</v-col>
			</v-row>
		</v-card-text>
		<v-container class="px-4">
			<v-row dense>
				<v-col cols="6" order="1">
					<v-btn elevation="0" class="cancel-button-style capitalize-text" @click="closeDrawer">
						{{ $t('cancel') }}
					</v-btn>
				</v-col>
				<v-col cols="6" order="2">
					<v-btn block color="primary" :disabled="!canSave" @click="save">{{ $t('save') }}</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import { formatTimestamp } from '../../utils/utilities';
export default {
	name: 'EditInventoryItem',
	data () {
		return {
			editingItem: {
				itemModelId: null,
				serialNumber: '',
				assetTag: '',
				remoteConnectIdString: '',
				note: '',
				purchaseDate: null,
				warrantyEndDate: null,
				purchasePrice: null,
				purchaseCurrency: 'USD',
				sellPrice: null,
				sellCurrency: 'USD',
				trackingNumber: '',
				shipmentCost: null,
				shipmentCurrency: 'USD',
				shipmentDate: null,
				status: '',
				condition: '',
				organizationId: null,
				locationId: null,
			},
			editingItemTemplate: {
				itemModelId: null,
				serialNumber: '',
				assetTag: '',
				remoteConnectIdString: '',
				note: '',
				purchaseDate: null,
				warrantyEndDate: null,
				purchasePrice: null,
				purchaseCurrency: 'USD',
				sellPrice: null,
				sellCurrency: 'USD',
				trackingNumber: '',
				shipmentCost: null,
				shipmentCurrency: 'USD',
				shipmentDate: null,
				status: '',
				condition: '',
				organizationId: null,
				locationId: null,
			},
			priceDateMenu: false,
			warrantyEndDateMenu: false,
			shipmentDateMenu: false,
		};
	},
	computed: {
		...mapState({
			itemCatalogs: (state) => state.inventoryItemCatalogs.itemCatalogs,
			selectedItem: (state) => state.inventoryItems.selectedInventoryItem,
			itemStatusOptions: (state) => state.inventoryItems.itemStatusOptions,
			itemConditionOptions: (state) => state.inventoryItems.itemConditionOptions,
			medicalInstitutions: (state) => state.medicalInstitutions.medicalInstitutions,
			hospitals: (state) => state.hospitals.hospitals,
		}),
		canSave () {
			const validPrice = /^\d+(?:\.\d{1,2})?$/.test(String(this.editingItem.purchasePrice));
			return (
				this.editingItem.purchaseDate && validPrice && this.editingItem.purchaseCurrency && this.editingItem.itemModelId
			);
		},
		formattedPurchaseDate () {
			return this.editingItem.purchaseDate ? this.formatTimestamp(this.editingItem.purchaseDate) : '';
		},
		formattedShipmentDate () {
			return this.editingItem.shipmentDate ? this.formatTimestamp(this.editingItem.shipmentDate) : '';
		},
		formattedWarrantyEndDate () {
			return this.editingItem.warrantyEndDate ? this.formatTimestamp(this.editingItem.warrantyEndDate) : '';
		},
		priceRules () {
			return [
				(v) => (v !== null && v !== undefined && String(v).length > 0) || this.$t('required-field'),
				(v) => /^\d+(?:\.\d{1,2})?$/.test(String(v)) || this.$t('invalid-currency-format') || 'Invalid currency',
			];
		},
	},
	async mounted () {
		this.editingItem = { ...this.editingItemTemplate };
		this.editingItem = {
			...this.selectedItem,
			purchaseDate: this.selectedItem.purchaseDate || null,
			shipmentDate: this.selectedItem.shipmentDate || null,
			warrantyEndDate: this.selectedItem.warrantyEndDate || null,
		};
		const loaders = [];
		if (!this.itemCatalogs?.length) loaders.push(this.getItemCatalogs());
		if (!this.itemStatusOptions?.length) loaders.push(this.getInventoryItemStatusOptions());
		if (!this.itemConditionOptions?.length) loaders.push(this.getInventoryItemConditionOptions());
		if (loaders.length) await Promise.all(loaders);
	},
	methods: {
		...mapActions({
			getItemCatalogs: 'inventoryItemCatalogs/getItemCatalogs',
			getInventoryItems: 'inventoryItems/getInventoryItems',
			editInventoryItem: 'inventoryItems/editInventoryItem',
			getInventoryItemStatusOptions: 'inventoryItems/getInventoryItemStatusOptions',
			getInventoryItemConditionOptions: 'inventoryItems/getInventoryItemConditionOptions',
		}),
		formatTimestamp,
		onPurchasePriceInput (val) {
			if (val === null || val === undefined) {
				this.editingItem.purchasePrice = '';
				return;
			}
			let s = String(val).replace(/[^0-9.]/g, '');
			const parts = s.split('.');
			if (parts.length > 1) {
				const intPart = parts.shift();
				const dec = parts.join('');
				s = intPart + (dec ? '.' + dec : '');
			}
			if (s.includes('.')) {
				const [intPart, decPart] = s.split('.');
				s = intPart + '.' + (decPart ? decPart.slice(0, 2) : '');
			}
			this.editingItem.purchasePrice = s;
		},
		onSellingPriceInput (val) {
			if (val === null || val === undefined) {
				this.editingItem.sellPrice = '';
				return;
			}
			let s = String(val).replace(/[^0-9.]/g, '');
			const parts = s.split('.');
			if (parts.length > 1) {
				const intPart = parts.shift();
				const dec = parts.join('');
				s = intPart + (dec ? '.' + dec : '');
			}
			if (s.includes('.')) {
				const [intPart, decPart] = s.split('.');
				s = intPart + '.' + (decPart ? decPart.slice(0, 2) : '');
			}
			this.editingItem.sellPrice = s;
		},
		onShipmentPriceInput (val) {
			if (val === null || val === undefined) {
				this.editingItem.shipmentCost = '';
				return;
			}
			let s = String(val).replace(/[^0-9.]/g, '');
			const parts = s.split('.');
			if (parts.length > 1) {
				const intPart = parts.shift();
				const dec = parts.join('');
				s = intPart + (dec ? '.' + dec : '');
			}
			if (s.includes('.')) {
				const [intPart, decPart] = s.split('.');
				s = intPart + '.' + (decPart ? decPart.slice(0, 2) : '');
			}
			this.editingItem.shipmentCost = s;
		},
		closeDrawer () {
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', false);
		},
		async save () {
			const formatter = new Intl.DateTimeFormat('en-CA');
			const body = {
				...this.editingItem,
				purchasePrice: Number(this.editingItem.purchasePrice),
				purchaseDate: formatter.format(new Date(this.editingItem.purchaseDate)),
				warrantyEndDate: this.editingItem.warrantyEndDate
					? formatter.format(new Date(this.editingItem.warrantyEndDate))
					: null,
			};
			await this.editInventoryItem(body);
			await this.getInventoryItems();
			this.closeDrawer();
		},
	},
	watch: {
		selectedItem (val) {
			if (val) {
				this.editingItem = { ...this.editingItemTemplate };
				this.editingItem = {
					...val,
					purchaseDate: val.purchaseDate || null,
					shipmentDate: val.shipmentDate || null,
					warrantyEndDate: val.warrantyEndDate || null,
				};
			}
		},
	},
};
</script>
